import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { env } from '../config/env';
import { Campaign, DataSource, Lead, OperationsStatus } from '../models/types';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  listCampaigns(): Promise<Campaign[]> {
    return firstValueFrom(this.http.get<Campaign[]>(`${env.apiUrl}/api/v1/campaigns`));
  }

  getCampaign(id: string): Promise<Campaign> {
    return firstValueFrom(this.http.get<Campaign>(`${env.apiUrl}/api/v1/campaigns/${id}`));
  }

  createCampaign(body: Partial<Campaign>): Promise<Campaign> {
    return firstValueFrom(this.http.post<Campaign>(`${env.apiUrl}/api/v1/campaigns`, body));
  }

  updateCampaign(id: string, body: Partial<Campaign>): Promise<Campaign> {
    return firstValueFrom(this.http.patch<Campaign>(`${env.apiUrl}/api/v1/campaigns/${id}`, body));
  }

  deleteCampaign(id: string): Promise<{ ok: boolean }> {
    return firstValueFrom(this.http.delete<{ ok: boolean }>(`${env.apiUrl}/api/v1/campaigns/${id}`));
  }

  listDataSources(): Promise<DataSource[]> {
    return firstValueFrom(this.http.get<DataSource[]>(`${env.apiUrl}/api/v1/data-sources`));
  }

  createDataSource(body: Partial<DataSource>): Promise<DataSource> {
    return firstValueFrom(this.http.post<DataSource>(`${env.apiUrl}/api/v1/data-sources`, body));
  }

  listLeads(campaignId: string, opts?: { status?: string; disposition?: string }): Promise<Lead[]> {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.disposition) params.set('disposition', opts.disposition);
    const q = params.toString() ? `?${params}` : '';
    return firstValueFrom(this.http.get<Lead[]>(`${env.apiUrl}/api/v1/campaigns/${campaignId}/leads${q}`));
  }

  dispositionSummary(campaignId: string): Promise<{
    dispositions: Array<{ id: string; label: string; count: number }>;
    untagged: number;
  }> {
    return firstValueFrom(
      this.http.get<{
        dispositions: Array<{ id: string; label: string; count: number }>;
        untagged: number;
      }>(`${env.apiUrl}/api/v1/campaigns/${campaignId}/disposition-summary`),
    );
  }

  resetLeads(campaignId: string, scope: 'all' | 'selected', leadIds: string[] = []): Promise<any> {
    return firstValueFrom(this.http.post(`${env.apiUrl}/api/v1/campaigns/${campaignId}/leads/reset`,
      {scope, lead_ids: leadIds, confirmed: true}));
  }

  listFiles(campaignId: string): Promise<Array<{ id: string; filename: string; chars: number }>> {
    return firstValueFrom(
      this.http.get<Array<{ id: string; filename: string; chars: number }>>(
        `${env.apiUrl}/api/v1/campaigns/${campaignId}/files`,
      ),
    );
  }

  uploadFile(campaignId: string, file: File): Promise<unknown> {
    const fd = new FormData();
    fd.append('file', file);
    return firstValueFrom(this.http.post(`${env.apiUrl}/api/v1/campaigns/${campaignId}/files`, fd));
  }

  deleteFile(campaignId: string, fileId: string): Promise<{ ok: boolean }> {
    return firstValueFrom(
      this.http.delete<{ ok: boolean }>(`${env.apiUrl}/api/v1/campaigns/${campaignId}/files/${fileId}`),
    );
  }

  deleteLead(campaignId: string, leadId: string): Promise<{ ok: boolean }> {
    return firstValueFrom(
      this.http.delete<{ ok: boolean }>(`${env.apiUrl}/api/v1/campaigns/${campaignId}/leads/${leadId}`),
    );
  }

  updateLead(
    campaignId: string,
    leadId: string,
    data: { name?: string | null; address?: string | null; gender?: string | null },
  ): Promise<Lead> {
    return firstValueFrom(
      this.http.patch<Lead>(`${env.apiUrl}/api/v1/campaigns/${campaignId}/leads/${leadId}`, data),
    );
  }

  queueStatus(campaignId?: string): Promise<{
    summary: string;
    ready_count: number;
    campaigns: Array<{
      campaign_id: string;
      name: string;
      status: string;
      pending_leads: number;
      in_calling_window: boolean;
      window: string;
      timezone: string;
      can_dial_now: boolean;
      blockers: string[];
    }>;
  }> {
    const params = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
    return firstValueFrom(
      this.http.get<{
        summary: string;
        ready_count: number;
        campaigns: Array<{
          campaign_id: string;
          name: string;
          status: string;
          pending_leads: number;
          in_calling_window: boolean;
          window: string;
          timezone: string;
          can_dial_now: boolean;
          blockers: string[];
        }>;
      }>(`${env.apiUrl}/api/v1/telephony/queue-status${params}`),
    );
  }

  operations(campaignId?: string): Promise<OperationsStatus> {
    const params = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
    return firstValueFrom(
      this.http.get<OperationsStatus>(`${env.apiUrl}/api/v1/telephony/operations${params}`),
    );
  }

  analyticsSummary(days = 30, campaignId?: string): Promise<any> {
    const params = new URLSearchParams({ days: String(days) });
    if (campaignId) params.set('campaign_id', campaignId);
    return firstValueFrom(
      this.http.get(`${env.apiUrl}/api/v1/analytics/summary?${params.toString()}`),
    );
  }

  testFormPush(campaignId: string): Promise<{
    ok: boolean;
    queued: boolean;
    message: string;
    target_url: string;
    pushed_fields: Record<string, unknown>;
  }> {
    return firstValueFrom(
      this.http.post<{
        ok: boolean;
        queued: boolean;
        message: string;
        target_url: string;
        pushed_fields: Record<string, unknown>;
      }>(`${env.apiUrl}/api/v1/telephony/test-form-push/${campaignId}`, {}),
    );
  }

  importCsv(
    campaignId: string,
    file: File,
    mapping: Record<string, unknown>,
  ): Promise<{ imported: number; skipped_duplicates: number; missing_gender?: number; total_rows: number }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mapping', JSON.stringify(mapping));
    return firstValueFrom(
      this.http.post<{ imported: number; skipped_duplicates: number; missing_gender?: number; total_rows: number }>(
        `${env.apiUrl}/api/v1/campaigns/${campaignId}/leads/csv`,
        fd,
      ),
    );
  }
}
